// SPDX-License-Identifier: AGPL-3.0-or-later

export const LIMIT_KEYS = [
	'max_guild_video_fps',
	'max_guild_video_height',
	'max_guild_soundboard_sounds',
	'feature_role_gradient',
	'feature_role_icons',
	'feature_announcement_channels',
	'feature_guild_tags',
	'feature_guild_notification_sound',
	'feature_guild_wallpaper',
	'max_followers_per_announcement_channel',
	'avatar_max_size',
	'emoji_max_size',
	'feature_animated_avatar',
	'feature_animated_banner',
	'feature_app_commands',
	'feature_bot_interactions',
	'feature_bot_components',
	'feature_bot_select_menus',
	'feature_bot_modals',
	'feature_bot_components_v2',
	'feature_bot_interactions_blocked',
	'feature_custom_discriminator',
	'feature_custom_notification_sounds',
	'feature_early_access',
	'feature_feed',
	'feature_feed_categories',
	'feature_feed_comment_like_notifications',
	'feature_feed_discover',
	'feature_feed_discover_media',
	'feature_feed_discover_video',
	'feature_feed_new',
	'feature_feed_post_like_notifications',
	'feature_feed_reply_notifications',
	'feature_feed_suggestions',
	'feature_feed_scheduled',
	'feature_feed_topics',
	'feature_feed_video',
	'feature_feed_views',
	'feature_guild_roster',
	'feature_mature_content_settings',
	'feature_micupbot',
	'feature_name_style',
	'feature_profile_theme',
	'feature_shop_boost_payriff',
	'feature_shop_boost_subscription',
	'feature_global_expressions',
	'feature_guild_create',
	'feature_higher_video_quality',
	'feature_per_guild_profiles',
	'feature_table_game_chance_rewards',
	'feature_table_game_ranked',
	'feature_voice_entrance_sounds',
	'feed_fanout_max_followers',
	'feed_fanout_max_posts_per_day',
	'feed_fanout_min_followers',
	'feed_suggestions_min_followers',
	'max_attachments_per_message',
	'max_bio_length',
	'max_bookmarks',
	'max_boost_slots',
	'max_channels_per_category',
	'max_comments_per_post',
	'max_custom_backgrounds',
	'max_embeds_per_message',
	'max_favorite_meme_tags',
	'max_favorite_memes',
	'max_feed_posts_per_day',
	'max_feed_scheduled_posts',
	'max_feed_image_size',
	'max_feed_video_size',
	'max_feed_video_bytes_per_day',
	'max_feed_video_duration',
	'max_following',
	'max_group_dm_recipients',
	'max_group_dms_per_user',
	'max_guild_channels',
	'max_guild_emojis',
	'max_guild_emojis_animated_more',
	'max_guild_emojis_animated',
	'max_guild_emojis_static_more',
	'max_guild_emojis_static',
	'max_guild_invites',
	'max_guild_members',
	'max_guild_roles',
	'max_guild_stickers_more',
	'max_guild_stickers',
	'max_guilds',
	'max_attachment_file_size',
	'max_message_length',
	'max_private_channels_per_user',
	'max_purchased_boost_slots',
	'max_reactions_per_message',
	'max_relationships',
	'max_users_per_message_reaction',
	'max_voice_message_duration',
	'max_webhooks_per_channel',
	'max_webhooks_per_guild',
	'sticker_max_size',
	'max_new_dms_per_hour',
	'max_new_dms_per_day',
	'max_new_dms_per_hour_new_account',
	'max_new_dms_per_day_new_account',
	'max_new_dms_per_hour_bot',
	'max_new_dms_per_day_bot',
	'new_account_age_hours',
	'new_dm_captcha_after',
	'spam_short_window_minutes',
	'dm_duplicate_freeze_after',
	'dm_open_alert_per_hour',
	'max_guild_joins_per_hour_new_account',
	'max_guild_joins_per_day_new_account',
	'cross_channel_spam_block_after',
	'cross_channel_spam_freeze_after',
	'cross_channel_spam_block_after_bot',
] as const;

export type LimitKey = (typeof LIMIT_KEYS)[number];
export type LimitScope = 'user' | 'guild' | 'both';

export const LIMIT_KEY_SCOPES: Record<LimitKey, LimitScope> = {
	max_guild_video_fps: 'guild',
	max_guild_video_height: 'guild',
	max_guild_soundboard_sounds: 'guild',
	feature_role_gradient: 'guild',
	feature_role_icons: 'guild',
	feature_announcement_channels: 'guild',
	feature_guild_tags: 'user',
	feature_guild_notification_sound: 'guild',
	feature_guild_wallpaper: 'guild',
	max_followers_per_announcement_channel: 'guild',
	avatar_max_size: 'user',
	emoji_max_size: 'guild',
	feature_animated_avatar: 'user',
	feature_animated_banner: 'user',
	feature_app_commands: 'user',
	feature_bot_interactions: 'user',
	feature_bot_components: 'user',
	feature_bot_select_menus: 'user',
	feature_bot_modals: 'user',
	feature_bot_components_v2: 'user',
	feature_bot_interactions_blocked: 'user',
	feature_custom_discriminator: 'user',
	feature_custom_notification_sounds: 'user',
	feature_early_access: 'user',
	feature_feed: 'user',
	feature_feed_categories: 'user',
	feature_feed_comment_like_notifications: 'user',
	feature_feed_discover: 'user',
	feature_feed_discover_media: 'user',
	feature_feed_discover_video: 'user',
	feature_feed_new: 'user',
	feature_feed_post_like_notifications: 'user',
	feature_feed_reply_notifications: 'user',
	feature_feed_suggestions: 'user',
	feature_feed_scheduled: 'user',
	feature_feed_topics: 'user',
	feature_feed_video: 'user',
	feature_feed_views: 'user',
	feature_guild_roster: 'user',
	feature_mature_content_settings: 'user',
	feature_micupbot: 'user',
	feature_name_style: 'user',
	feature_profile_theme: 'user',
	feature_shop_boost_payriff: 'user',
	feature_shop_boost_subscription: 'user',
	feature_global_expressions: 'user',
	feature_guild_create: 'user',
	feature_higher_video_quality: 'user',
	feature_per_guild_profiles: 'user',
	feature_table_game_chance_rewards: 'user',
	feature_table_game_ranked: 'user',
	feature_voice_entrance_sounds: 'user',
	feed_fanout_max_followers: 'user',
	feed_fanout_max_posts_per_day: 'user',
	feed_fanout_min_followers: 'user',
	feed_suggestions_min_followers: 'user',
	max_attachments_per_message: 'both',
	max_bio_length: 'user',
	max_bookmarks: 'user',
	max_boost_slots: 'user',
	max_purchased_boost_slots: 'user',
	max_channels_per_category: 'guild',
	max_comments_per_post: 'user',
	max_custom_backgrounds: 'user',
	max_embeds_per_message: 'both',
	max_favorite_meme_tags: 'user',
	max_favorite_memes: 'user',
	max_feed_posts_per_day: 'user',
	max_feed_scheduled_posts: 'user',
	max_feed_image_size: 'user',
	max_feed_video_size: 'user',
	max_feed_video_bytes_per_day: 'user',
	max_feed_video_duration: 'user',
	max_following: 'user',
	max_group_dm_recipients: 'user',
	max_group_dms_per_user: 'user',
	max_guild_channels: 'guild',
	max_guild_emojis: 'guild',
	max_guild_emojis_animated_more: 'guild',
	max_guild_emojis_animated: 'guild',
	max_guild_emojis_static_more: 'guild',
	max_guild_emojis_static: 'guild',
	max_guild_invites: 'guild',
	max_guild_members: 'guild',
	max_guild_roles: 'guild',
	max_guild_stickers_more: 'guild',
	max_guild_stickers: 'guild',
	max_guilds: 'user',
	max_attachment_file_size: 'both',
	max_message_length: 'both',
	max_private_channels_per_user: 'user',
	max_reactions_per_message: 'guild',
	max_relationships: 'user',
	max_users_per_message_reaction: 'guild',
	max_voice_message_duration: 'both',
	max_webhooks_per_channel: 'guild',
	max_webhooks_per_guild: 'guild',
	sticker_max_size: 'guild',
	max_new_dms_per_hour: 'user',
	max_new_dms_per_day: 'user',
	max_new_dms_per_hour_new_account: 'user',
	max_new_dms_per_day_new_account: 'user',
	max_new_dms_per_hour_bot: 'user',
	max_new_dms_per_day_bot: 'user',
	new_account_age_hours: 'user',
	new_dm_captcha_after: 'user',
	spam_short_window_minutes: 'user',
	dm_duplicate_freeze_after: 'user',
	dm_open_alert_per_hour: 'user',
	max_guild_joins_per_hour_new_account: 'user',
	max_guild_joins_per_day_new_account: 'user',
	cross_channel_spam_block_after: 'user',
	cross_channel_spam_freeze_after: 'user',
	cross_channel_spam_block_after_bot: 'user',
};

type LimitCategory = 'messages' | 'guilds' | 'channels' | 'expressions' | 'files' | 'social' | 'features';

interface LimitKeyMetadata {
	key: LimitKey;
	label: string;
	description: string;
	category: LimitCategory;
	scope: LimitScope;
	isToggle: boolean;
	unit?: 'bytes' | 'count';
	min?: number;
	max?: number;
}

export const LIMIT_CATEGORY_LABELS: Record<LimitCategory, string> = {
	messages: 'Messages',
	guilds: 'Guilds',
	channels: 'Channels',
	expressions: 'Expressions & Stickers',
	files: 'Files & Attachments',
	social: 'Social & Private',
	features: 'Feature Toggles',
};
export const LIMIT_KEY_METADATA: Record<LimitKey, LimitKeyMetadata> = {
	max_guild_video_fps: {
		key: 'max_guild_video_fps',
		label: 'Max Guild Video FPS',
		description: 'Maximum video frames per second granted by the guild',
		category: 'guilds',
		scope: 'guild',
		isToggle: false,
		unit: 'count',
	},
	max_guild_video_height: {
		key: 'max_guild_video_height',
		label: 'Max Guild Video Height',
		description: 'Maximum video height in pixels granted by the guild',
		category: 'guilds',
		scope: 'guild',
		isToggle: false,
		unit: 'count',
	},
	max_guild_soundboard_sounds: {
		key: 'max_guild_soundboard_sounds',
		label: 'Max Soundboard Sounds',
		description: 'Maximum soundboard sounds per guild',
		category: 'expressions',
		scope: 'guild',
		isToggle: false,
		unit: 'count',
	},
	feature_role_gradient: {
		key: 'feature_role_gradient',
		label: 'Role Gradient',
		description: 'Assign a second static color to roles',
		category: 'features',
		scope: 'guild',
		isToggle: true,
		min: 0,
		max: 1,
	},
	feature_role_icons: {
		key: 'feature_role_icons',
		label: 'Role Icons',
		description: 'Assign Unicode or guild emoji icons to roles',
		category: 'features',
		scope: 'guild',
		isToggle: true,
		min: 0,
		max: 1,
	},
	feature_announcement_channels: {
		key: 'feature_announcement_channels',
		label: 'Announcement Channels',
		description:
			'Create announcement channels, follow them and publish to their followers. Off by default, 0 is the emergency stop',
		category: 'features',
		scope: 'guild',
		isToggle: true,
		min: 0,
		max: 1,
	},
	feature_guild_tags: {
		key: 'feature_guild_tags',
		label: 'Guild Tags',
		description:
			'Instance-wide: setting, wearing and showing guild tags; 0 stops them, stored tags stay. Boost level 3 still decides which guilds have one',
		category: 'features',
		scope: 'user',
		isToggle: true,
	},
	feature_guild_notification_sound: {
		key: 'feature_guild_notification_sound',
		label: 'Guild Notification Sound',
		description: 'Set a message notification sound for the guild and its channels',
		category: 'features',
		scope: 'guild',
		isToggle: true,
		min: 0,
		max: 1,
	},
	feature_guild_wallpaper: {
		key: 'feature_guild_wallpaper',
		label: 'Guild Wallpaper',
		description: 'Set a still image or a colour behind the messages of text channels',
		category: 'features',
		scope: 'guild',
		isToggle: true,
		min: 0,
		max: 1,
	},
	max_followers_per_announcement_channel: {
		key: 'max_followers_per_announcement_channel',
		label: 'Max Followers per Announcement Channel',
		description: 'Maximum channels that can follow one announcement channel',
		category: 'channels',
		scope: 'guild',
		isToggle: false,
		unit: 'count',
	},
	feature_app_commands: {
		key: 'feature_app_commands',
		label: 'Application Commands',
		description:
			'Evaluated on the calling user: command catalog, running commands, clicking third-party bot components and submitting modals. Emergency off switch',
		category: 'features',
		scope: 'user',
		isToggle: true,
	},
	feature_bot_interactions: {
		key: 'feature_bot_interactions',
		label: 'Bot Interactions',
		description:
			'Evaluated on the bot user: command registration, catalog, interaction delivery, callbacks, followups and components. Emergency off switch for every bot; feature_bot_interactions_blocked stops a single bot',
		category: 'features',
		scope: 'user',
		isToggle: true,
	},
	feature_bot_components: {
		key: 'feature_bot_components',
		label: 'Bot Buttons',
		description:
			'Evaluated on the bot user: write buttons and link buttons and receive their clicks. Emergency off switch',
		category: 'features',
		scope: 'user',
		isToggle: true,
	},
	feature_bot_select_menus: {
		key: 'feature_bot_select_menus',
		label: 'Bot Select Menus',
		description: 'Evaluated on the bot user: write select menus and receive their selections. Emergency off switch',
		category: 'features',
		scope: 'user',
		isToggle: true,
	},
	feature_bot_modals: {
		key: 'feature_bot_modals',
		label: 'Bot Modals',
		description: 'Evaluated on the bot user: answer with a modal and receive its submission. Emergency off switch',
		category: 'features',
		scope: 'user',
		isToggle: true,
	},
	feature_bot_components_v2: {
		key: 'feature_bot_components_v2',
		label: 'Bot Components v2',
		description:
			'Evaluated on the bot user: send messages flagged IS_COMPONENTS_V2. Off by default until every client draws the v2 kinds (older clients fail on them)',
		category: 'features',
		scope: 'user',
		isToggle: true,
	},
	feature_bot_interactions_blocked: {
		key: 'feature_bot_interactions_blocked',
		label: 'Block Bot Interactions',
		description:
			'Evaluated on the bot user: 1 stops everything feature_bot_interactions covers for this bot. Give the bot the interactions_blocked trait with a rule setting this to 1; trait rules can only raise a value',
		category: 'features',
		scope: 'user',
		isToggle: true,
	},
	max_message_length: {
		key: 'max_message_length',
		label: 'Max Message Length',
		description: 'Maximum number of characters per message',
		category: 'messages',
		scope: 'both',
		isToggle: false,
		unit: 'count',
	},
	max_attachments_per_message: {
		key: 'max_attachments_per_message',
		label: 'Max Attachments per Message',
		description: 'Maximum number of file attachments per message',
		category: 'messages',
		scope: 'both',
		isToggle: false,
		unit: 'count',
	},
	max_embeds_per_message: {
		key: 'max_embeds_per_message',
		label: 'Max Embeds per Message',
		description: 'Maximum number of embeds per message',
		category: 'messages',
		scope: 'both',
		isToggle: false,
		unit: 'count',
	},
	max_reactions_per_message: {
		key: 'max_reactions_per_message',
		label: 'Max Reactions per Message',
		description: 'Maximum distinct reactions per message',
		category: 'messages',
		scope: 'guild',
		isToggle: false,
		unit: 'count',
	},
	max_users_per_message_reaction: {
		key: 'max_users_per_message_reaction',
		label: 'Max Users per Reaction',
		description: 'Maximum users who can use the same reaction on a message',
		category: 'messages',
		scope: 'guild',
		isToggle: false,
		unit: 'count',
	},
	max_voice_message_duration: {
		key: 'max_voice_message_duration',
		label: 'Max Voice Message Duration',
		description: 'Maximum duration in seconds for voice message recordings',
		category: 'messages',
		scope: 'both',
		isToggle: false,
		unit: 'count',
	},
	max_guilds: {
		key: 'max_guilds',
		label: 'Max Guilds',
		description: 'Maximum number of guilds a user can join',
		category: 'guilds',
		scope: 'user',
		isToggle: false,
		unit: 'count',
	},
	max_guild_members: {
		key: 'max_guild_members',
		label: 'Max Guild Members',
		description: 'Maximum members per guild',
		category: 'guilds',
		scope: 'guild',
		isToggle: false,
		unit: 'count',
	},
	max_guild_roles: {
		key: 'max_guild_roles',
		label: 'Max Guild Roles',
		description: 'Maximum roles per guild',
		category: 'guilds',
		scope: 'guild',
		isToggle: false,
		unit: 'count',
	},
	max_guild_invites: {
		key: 'max_guild_invites',
		label: 'Max Guild Invites',
		description: 'Maximum active invites per guild',
		category: 'guilds',
		scope: 'guild',
		isToggle: false,
		unit: 'count',
	},
	max_guild_channels: {
		key: 'max_guild_channels',
		label: 'Max Guild Channels',
		description: 'Maximum channels per guild',
		category: 'channels',
		scope: 'guild',
		isToggle: false,
		unit: 'count',
	},
	max_channels_per_category: {
		key: 'max_channels_per_category',
		label: 'Max Channels per Category',
		description: 'Maximum channels per category',
		category: 'channels',
		scope: 'guild',
		isToggle: false,
		unit: 'count',
	},
	max_webhooks_per_channel: {
		key: 'max_webhooks_per_channel',
		label: 'Max Webhooks per Channel',
		description: 'Maximum webhooks per channel',
		category: 'channels',
		scope: 'guild',
		isToggle: false,
		unit: 'count',
	},
	max_webhooks_per_guild: {
		key: 'max_webhooks_per_guild',
		label: 'Max Webhooks per Guild',
		description: 'Maximum webhooks per guild',
		category: 'channels',
		scope: 'guild',
		isToggle: false,
		unit: 'count',
	},
	max_guild_emojis: {
		key: 'max_guild_emojis',
		label: 'Max Emojis',
		description: 'Maximum total emojis per guild',
		category: 'expressions',
		scope: 'guild',
		isToggle: false,
		unit: 'count',
	},
	max_guild_emojis_static: {
		key: 'max_guild_emojis_static',
		label: 'Max Emojis (Legacy Static)',
		description: 'Compatibility alias for older clients; emoji limits are enforced as a shared total',
		category: 'expressions',
		scope: 'guild',
		isToggle: false,
		unit: 'count',
	},
	max_guild_emojis_animated: {
		key: 'max_guild_emojis_animated',
		label: 'Max Emojis (Legacy Animated)',
		description: 'Compatibility alias for older clients; emoji limits are enforced as a shared total',
		category: 'expressions',
		scope: 'guild',
		isToggle: false,
		unit: 'count',
	},
	max_guild_emojis_static_more: {
		key: 'max_guild_emojis_static_more',
		label: 'Max Emojis (Legacy Static More)',
		description: 'Compatibility alias for older clients; emoji limits are enforced as a shared total',
		category: 'expressions',
		scope: 'guild',
		isToggle: false,
		unit: 'count',
	},
	max_guild_emojis_animated_more: {
		key: 'max_guild_emojis_animated_more',
		label: 'Max Emojis (Legacy Animated More)',
		description: 'Compatibility alias for older clients; emoji limits are enforced as a shared total',
		category: 'expressions',
		scope: 'guild',
		isToggle: false,
		unit: 'count',
	},
	max_guild_stickers: {
		key: 'max_guild_stickers',
		label: 'Max Stickers',
		description: 'Maximum stickers per guild',
		category: 'expressions',
		scope: 'guild',
		isToggle: false,
		unit: 'count',
	},
	max_guild_stickers_more: {
		key: 'max_guild_stickers_more',
		label: 'Max Stickers (Legacy More)',
		description: 'Compatibility alias for older clients; sticker limits are enforced as one shared total',
		category: 'expressions',
		scope: 'guild',
		isToggle: false,
		unit: 'count',
	},
	emoji_max_size: {
		key: 'emoji_max_size',
		label: 'Emoji Max Size',
		description: 'Maximum file size for emoji uploads',
		category: 'expressions',
		scope: 'guild',
		isToggle: false,
		unit: 'bytes',
	},
	sticker_max_size: {
		key: 'sticker_max_size',
		label: 'Sticker Max Size',
		description: 'Maximum file size for sticker uploads',
		category: 'expressions',
		scope: 'guild',
		isToggle: false,
		unit: 'bytes',
	},
	max_attachment_file_size: {
		key: 'max_attachment_file_size',
		label: 'Max Attachment File Size',
		description: 'Maximum size of each attachment file',
		category: 'files',
		scope: 'both',
		isToggle: false,
		unit: 'bytes',
	},
	avatar_max_size: {
		key: 'avatar_max_size',
		label: 'Avatar Max Size',
		description: 'Maximum file size for avatar uploads',
		category: 'files',
		scope: 'user',
		isToggle: false,
		unit: 'bytes',
	},
	max_custom_backgrounds: {
		key: 'max_custom_backgrounds',
		label: 'Max Custom Backgrounds',
		description: 'Maximum custom profile backgrounds',
		category: 'files',
		scope: 'user',
		isToggle: false,
		unit: 'count',
	},
	max_relationships: {
		key: 'max_relationships',
		label: 'Max Relationships',
		description: 'Maximum friends/blocked users',
		category: 'social',
		scope: 'user',
		isToggle: false,
		unit: 'count',
	},
	max_private_channels_per_user: {
		key: 'max_private_channels_per_user',
		label: 'Max Private Channels',
		description: 'Maximum DM channels per user',
		category: 'social',
		scope: 'user',
		isToggle: false,
		unit: 'count',
	},
	max_group_dms_per_user: {
		key: 'max_group_dms_per_user',
		label: 'Max Group DMs',
		description: 'Maximum group DMs a user can own',
		category: 'social',
		scope: 'user',
		isToggle: false,
		unit: 'count',
	},
	max_group_dm_recipients: {
		key: 'max_group_dm_recipients',
		label: 'Max Group DM Members',
		description: 'Maximum members per group DM',
		category: 'social',
		scope: 'user',
		isToggle: false,
		unit: 'count',
	},
	max_bio_length: {
		key: 'max_bio_length',
		label: 'Max Bio Length',
		description: 'Maximum characters in user bio',
		category: 'social',
		scope: 'user',
		isToggle: false,
		unit: 'count',
	},
	max_bookmarks: {
		key: 'max_bookmarks',
		label: 'Max Bookmarks',
		description: 'Maximum bookmarked messages',
		category: 'social',
		scope: 'user',
		isToggle: false,
		unit: 'count',
	},
	max_favorite_memes: {
		key: 'max_favorite_memes',
		label: 'Max Favorite Memes',
		description: 'Maximum favorited memes',
		category: 'social',
		scope: 'user',
		isToggle: false,
		unit: 'count',
	},
	max_favorite_meme_tags: {
		key: 'max_favorite_meme_tags',
		label: 'Max Favorite Meme Tags',
		description: 'Maximum meme tags to track',
		category: 'social',
		scope: 'user',
		isToggle: false,
		unit: 'count',
	},
	max_following: {
		key: 'max_following',
		label: 'Max Following',
		description: 'Maximum number of users one account can follow',
		category: 'social',
		scope: 'user',
		isToggle: false,
		unit: 'count',
	},
	max_feed_posts_per_day: {
		key: 'max_feed_posts_per_day',
		label: 'Max Feed Posts per Day',
		description: 'Maximum feed posts one account can publish in a day; the server caps it at 150',
		category: 'social',
		scope: 'user',
		isToggle: false,
		unit: 'count',
	},
	max_feed_scheduled_posts: {
		key: 'max_feed_scheduled_posts',
		label: 'Max Scheduled Feed Posts',
		description: 'Feed posts one account can keep waiting for their time at once (feature_feed_scheduled)',
		category: 'social',
		scope: 'user',
		isToggle: false,
		unit: 'count',
	},
	max_feed_image_size: {
		key: 'max_feed_image_size',
		label: 'Max Feed Image Size',
		description: 'Maximum size of each feed post image and video poster; the server caps it at 50 MiB',
		category: 'files',
		scope: 'user',
		isToggle: false,
		unit: 'bytes',
	},
	max_feed_video_size: {
		key: 'max_feed_video_size',
		label: 'Max Feed Video Size',
		description:
			'Maximum size of one feed video, checked again on the stored original; the server caps it at 4 GiB. A started upload keeps the size it started with',
		category: 'files',
		scope: 'user',
		isToggle: false,
		unit: 'bytes',
	},
	max_feed_video_bytes_per_day: {
		key: 'max_feed_video_bytes_per_day',
		label: 'Max Feed Video Bytes per Day',
		description: 'Total size of the feed videos one account can upload in a day; the server caps it at 50 GiB',
		category: 'files',
		scope: 'user',
		isToggle: false,
		unit: 'bytes',
	},
	max_feed_video_duration: {
		key: 'max_feed_video_duration',
		label: 'Max Feed Video Duration',
		description:
			'Maximum length in seconds of one feed video, checked on what Bunny encoded; the server caps it at 3 hours. A started upload keeps the length it started with',
		category: 'files',
		scope: 'user',
		isToggle: false,
		unit: 'count',
	},
	feed_fanout_max_followers: {
		key: 'feed_fanout_max_followers',
		label: 'Feed Fan-out Follower Cap',
		description:
			'Follower count up to which new posts are written to every follower home feed; larger authors are read at request time',
		category: 'social',
		scope: 'user',
		isToggle: false,
		unit: 'count',
	},
	feed_fanout_min_followers: {
		key: 'feed_fanout_min_followers',
		label: 'Feed Fan-out Return Threshold',
		description:
			'An author read at request time goes back to follower home feed writes only once their follower count drops below this',
		category: 'social',
		scope: 'user',
		isToggle: false,
		unit: 'count',
	},
	feed_fanout_max_posts_per_day: {
		key: 'feed_fanout_max_posts_per_day',
		label: 'Feed Fan-out Posts per Day',
		description: 'Posts per author per day written to follower home feeds; later posts show only on the profile',
		category: 'social',
		scope: 'user',
		isToggle: false,
		unit: 'count',
	},
	feed_suggestions_min_followers: {
		key: 'feed_suggestions_min_followers',
		label: 'Feed Suggestions Follower Floor',
		description:
			'Instance-wide: the fewest followers an account needs to be suggested beside the feed (the list build reads the default rule); values below 1 count as 1',
		category: 'social',
		scope: 'user',
		isToggle: false,
		unit: 'count',
		min: 1,
	},
	max_comments_per_post: {
		key: 'max_comments_per_post',
		label: 'Max Comments per Post',
		description: 'Maximum comments one feed post can hold',
		category: 'social',
		scope: 'user',
		isToggle: false,
		unit: 'count',
	},
	max_boost_slots: {
		key: 'max_boost_slots',
		label: 'Max Boost Slots',
		description: 'Community boost slots an account holds without buying extra slots',
		category: 'guilds',
		scope: 'user',
		isToggle: false,
		unit: 'count',
	},
	max_purchased_boost_slots: {
		key: 'max_purchased_boost_slots',
		label: 'Max Bought Boost Slots',
		description: 'Bought boost slots an account may hold at once; a purchase past it is refused',
		category: 'guilds',
		scope: 'user',
		isToggle: false,
		unit: 'count',
	},
	feature_animated_avatar: {
		key: 'feature_animated_avatar',
		label: 'Animated Avatar',
		description: 'Allow animated avatar uploads',
		category: 'features',
		scope: 'user',
		isToggle: true,
	},
	feature_animated_banner: {
		key: 'feature_animated_banner',
		label: 'Animated Banner',
		description: 'Allow animated banner uploads',
		category: 'features',
		scope: 'user',
		isToggle: true,
	},
	feature_custom_discriminator: {
		key: 'feature_custom_discriminator',
		label: 'Custom Discriminator',
		description: 'Allow custom discriminator selection',
		category: 'features',
		scope: 'user',
		isToggle: true,
	},
	feature_custom_notification_sounds: {
		key: 'feature_custom_notification_sounds',
		label: 'Custom Notification Sounds',
		description: 'Allow custom notification sounds',
		category: 'features',
		scope: 'user',
		isToggle: true,
	},
	feature_early_access: {
		key: 'feature_early_access',
		label: 'Early Access',
		description: 'Access to beta features',
		category: 'features',
		scope: 'user',
		isToggle: true,
	},
	feature_feed: {
		key: 'feature_feed',
		label: 'Feed',
		description:
			'Post to, read and interact with the feed; 0 closes every feed route. Trait rules only raise limits, so turn it off in every rule where it is 1 (default, premium and any trait rule made from the defaults)',
		category: 'features',
		scope: 'user',
		isToggle: true,
	},
	feature_feed_categories: {
		key: 'feature_feed_categories',
		label: 'Feed Categories',
		description:
			"Show the feed categories the admins curate, file a post under one and read a category's public posts; 0 closes GET /feed/categories and the category pages, and a new post's category is ignored",
		category: 'features',
		scope: 'user',
		isToggle: true,
	},
	feature_feed_discover: {
		key: 'feature_feed_discover',
		label: 'Feed Discover',
		description: 'Read the Discover feed of popular public posts; 0 closes the route and empties the list',
		category: 'features',
		scope: 'user',
		isToggle: true,
	},
	feature_feed_discover_media: {
		key: 'feature_feed_discover_media',
		label: 'Feed Discover Media',
		description:
			'Instance-wide: let posts with images or video into Discover (the list build reads the default rule, not per user); stays 0 until media scanning is verified in production',
		category: 'features',
		scope: 'user',
		isToggle: true,
	},
	feature_feed_discover_video: {
		key: 'feature_feed_discover_video',
		label: 'Feed Discover Video',
		description:
			'Instance-wide: let public posts with a Bunny video into Discover; 0 until video scanning is verified in production',
		category: 'features',
		scope: 'user',
		isToggle: true,
	},
	feature_feed_new: {
		key: 'feature_feed_new',
		label: 'Feed New',
		description:
			'Offer Popular and Latest on Discover and open GET /feed/discover/new; 0 closes the route, and the list keeps filling',
		category: 'features',
		scope: 'user',
		isToggle: true,
	},
	feature_feed_comment_like_notifications: {
		key: 'feature_feed_comment_like_notifications',
		label: 'Feed Comment Like Notifications',
		description:
			'Instance-wide: record comment_like notifications; stays 0 until clients that ignore unknown notification types have shipped',
		category: 'features',
		scope: 'user',
		isToggle: true,
	},
	feature_feed_post_like_notifications: {
		key: 'feature_feed_post_like_notifications',
		label: 'Feed Post Like Notifications',
		description:
			'Instance-wide: record post_like notifications; 0 by default, so the activity inbox holds follows and comments only',
		category: 'features',
		scope: 'user',
		isToggle: true,
	},
	feature_feed_reply_notifications: {
		key: 'feature_feed_reply_notifications',
		label: 'Feed Reply Notifications',
		description:
			'Instance-wide: record comment_reply notifications; stays 0 until clients that ignore unknown notification types have shipped',
		category: 'features',
		scope: 'user',
		isToggle: true,
	},
	feature_feed_suggestions: {
		key: 'feature_feed_suggestions',
		label: 'Feed Suggestions',
		description:
			'Show popular accounts and Discovery communities beside the feed and let a stranger follow an account from that list; 0 closes GET /feed/suggestions and that follow path, the list build reads the default rule',
		category: 'features',
		scope: 'user',
		isToggle: true,
	},
	feature_feed_topics: {
		key: 'feature_feed_topics',
		label: 'Feed Topics',
		description:
			"Show the day's feed topics, file a post under one and read a topic's public posts; 0 closes GET /feed/topics and the topic pages, and a new post's topic is ignored",
		category: 'features',
		scope: 'user',
		isToggle: true,
	},
	feature_feed_scheduled: {
		key: 'feature_feed_scheduled',
		label: 'Feed Scheduled Posts',
		description:
			'Schedule a text or image feed post up to 30 days ahead; 0 hides scheduling and holds waiting posts (they publish once it is back on)',
		category: 'features',
		scope: 'user',
		isToggle: true,
	},
	feature_feed_video: {
		key: 'feature_feed_video',
		label: 'Feed Video',
		description:
			'Post one video (up to 500 MB) through Bunny Stream; stays 0 until the Bunny token and scanning settings are live',
		category: 'features',
		scope: 'user',
		isToggle: true,
	},
	feature_micupbot: {
		key: 'feature_micupbot',
		label: 'MicupBOT',
		description:
			'Instance-wide: the official community bot (welcome, auto roles, reaction roles, levels, # commands, automod); 0 stops every hook, job and settings write, stored settings stay',
		category: 'features',
		scope: 'user',
		isToggle: true,
	},
	feature_name_style: {
		key: 'feature_name_style',
		label: 'Name Style',
		description:
			'Set a name font with a solid or two-colour gradient name colour; 0 refuses setting one (removing stays allowed). A stored style shows only while the account has active premium',
		category: 'features',
		scope: 'user',
		isToggle: true,
	},
	feature_profile_theme: {
		key: 'feature_profile_theme',
		label: 'Profile Theme',
		description:
			'Paint the profile card with a two-colour gradient; 0 refuses setting one (removing stays allowed). A stored theme shows only while the account has active premium',
		category: 'features',
		scope: 'user',
		isToggle: true,
	},
	feature_shop_boost_payriff: {
		key: 'feature_shop_boost_payriff',
		label: 'Payriff Boost Sales',
		description:
			'Sell the 30 day boost slot to this account through Payriff; 0 hides the boost from a Payriff buyer and refuses the order. Paddle boost sales never read it',
		category: 'features',
		scope: 'user',
		isToggle: true,
	},
	feature_shop_boost_subscription: {
		key: 'feature_shop_boost_subscription',
		label: 'Boost Subscriptions',
		description:
			'Sell boosts to this account as one monthly Paddle subscription of several boosts; 0 keeps the single 30 day boost. An existing boost subscription can always change its quantity or cancel',
		category: 'features',
		scope: 'user',
		isToggle: true,
	},
	feature_table_game_chance_rewards: {
		key: 'feature_table_game_chance_rewards',
		label: 'Chance Game Paid Rewards',
		description: 'Instance-wide: Micup Plus season rewards for okey/101; 0 withholds them until legal review',
		category: 'features',
		scope: 'user',
		isToggle: true,
	},
	feature_table_game_ranked: {
		key: 'feature_table_game_ranked',
		label: 'Ranked Table Games',
		description:
			'Instance-wide: ranked tables, rating records, leaderboards and profile leagues; 0 stops ranked tables and recording, stored ratings stay',
		category: 'features',
		scope: 'user',
		isToggle: true,
	},
	feature_guild_roster: {
		key: 'feature_guild_roster',
		label: 'Guild Roster Bot',
		description:
			'Instance-wide: the official Micup Roster bot announces hourly rosters and moves members at :35; 0 stops announcements, checks, reaction joins and settings writes, stored events stay',
		category: 'features',
		scope: 'user',
		isToggle: true,
	},
	feature_mature_content_settings: {
		key: 'feature_mature_content_settings',
		label: 'Mature Content Settings',
		description:
			'Show the mature content and content warning settings of communities and channels; 0 hides them wherever none is set yet, stored values, age gates and media filters keep working',
		category: 'features',
		scope: 'user',
		isToggle: true,
	},
	feature_feed_views: {
		key: 'feature_feed_views',
		label: 'Feed Views',
		description:
			'Instance-wide: count and show how many times each feed post was seen; 0 hides the counts and closes POST /feed/views, the stored counts stay',
		category: 'features',
		scope: 'user',
		isToggle: true,
	},
	feature_global_expressions: {
		key: 'feature_global_expressions',
		label: 'Global Expressions',
		description: 'Use expressions across all guilds',
		category: 'features',
		scope: 'user',
		isToggle: true,
	},
	feature_guild_create: {
		key: 'feature_guild_create',
		label: 'Community Creation Access',
		description: 'Create communities while the instance restricts community creation',
		category: 'features',
		scope: 'user',
		isToggle: true,
	},
	feature_higher_video_quality: {
		key: 'feature_higher_video_quality',
		label: 'Higher Video Quality',
		description: 'Access to higher video streaming quality',
		category: 'features',
		scope: 'user',
		isToggle: true,
	},
	feature_per_guild_profiles: {
		key: 'feature_per_guild_profiles',
		label: 'Per-Guild Profiles',
		description: 'Different profile per guild',
		category: 'features',
		scope: 'user',
		isToggle: true,
	},
	feature_voice_entrance_sounds: {
		key: 'feature_voice_entrance_sounds',
		label: 'Voice Entrance Sounds',
		description: 'Play sound when joining voice',
		category: 'features',
		scope: 'user',
		isToggle: true,
	},
	max_new_dms_per_hour: {
		key: 'max_new_dms_per_hour',
		label: 'Max New DMs per Hour',
		description:
			'New one to one conversations an established account can start in an hour; friends and replies are not counted (0 = no limit)',
		category: 'social',
		scope: 'user',
		isToggle: false,
		unit: 'count',
	},
	max_new_dms_per_day: {
		key: 'max_new_dms_per_day',
		label: 'Max New DMs per Day',
		description: 'New one to one conversations an established account can start in a day (0 = no limit)',
		category: 'social',
		scope: 'user',
		isToggle: false,
		unit: 'count',
	},
	max_new_dms_per_hour_new_account: {
		key: 'max_new_dms_per_hour_new_account',
		label: 'Max New DMs per Hour (New Account)',
		description:
			'New one to one conversations an account younger than the new account age can start in an hour (0 = no limit)',
		category: 'social',
		scope: 'user',
		isToggle: false,
		unit: 'count',
	},
	max_new_dms_per_day_new_account: {
		key: 'max_new_dms_per_day_new_account',
		label: 'Max New DMs per Day (New Account)',
		description:
			'New one to one conversations an account younger than the new account age can start in a day (0 = no limit)',
		category: 'social',
		scope: 'user',
		isToggle: false,
		unit: 'count',
	},
	max_new_dms_per_hour_bot: {
		key: 'max_new_dms_per_hour_bot',
		label: 'Max New DMs per Hour (Bot)',
		description: 'New one to one conversations a bot can start in an hour (0 = no limit)',
		category: 'social',
		scope: 'user',
		isToggle: false,
		unit: 'count',
	},
	max_new_dms_per_day_bot: {
		key: 'max_new_dms_per_day_bot',
		label: 'Max New DMs per Day (Bot)',
		description: 'New one to one conversations a bot can start in a day (0 = no limit)',
		category: 'social',
		scope: 'user',
		isToggle: false,
		unit: 'count',
	},
	new_account_age_hours: {
		key: 'new_account_age_hours',
		label: 'New Account Age (Hours)',
		description: 'Accounts younger than this many hours use the new account DM and community join limits',
		category: 'social',
		scope: 'user',
		isToggle: false,
		unit: 'count',
	},
	new_dm_captcha_after: {
		key: 'new_dm_captcha_after',
		label: 'New DM Captcha After',
		description:
			'New conversations within the short window after which each further one asks for a captcha (0 = off; bots never get a captcha)',
		category: 'social',
		scope: 'user',
		isToggle: false,
		unit: 'count',
	},
	spam_short_window_minutes: {
		key: 'spam_short_window_minutes',
		label: 'Spam Short Window (Minutes)',
		description: 'Window for the new DM captcha and the cross channel spam counts',
		category: 'social',
		scope: 'user',
		isToggle: false,
		unit: 'count',
	},
	dm_duplicate_freeze_after: {
		key: 'dm_duplicate_freeze_after',
		label: 'Duplicate DM Freeze After',
		description:
			'New conversations opened with the same text, invite or link after which the next one is blocked and the account frozen (0 = off)',
		category: 'social',
		scope: 'user',
		isToggle: false,
		unit: 'count',
	},
	dm_open_alert_per_hour: {
		key: 'dm_open_alert_per_hour',
		label: 'DM Open Alert per Hour',
		description:
			'New conversation attempts in an hour that file one admin alert report, exempt accounts included (0 = off)',
		category: 'social',
		scope: 'user',
		isToggle: false,
		unit: 'count',
	},
	max_guild_joins_per_hour_new_account: {
		key: 'max_guild_joins_per_hour_new_account',
		label: 'Max Community Joins per Hour (New Account)',
		description: 'Communities a new account can join by itself in an hour (0 = no limit)',
		category: 'social',
		scope: 'user',
		isToggle: false,
		unit: 'count',
	},
	max_guild_joins_per_day_new_account: {
		key: 'max_guild_joins_per_day_new_account',
		label: 'Max Community Joins per Day (New Account)',
		description: 'Communities a new account can join by itself in a day (0 = no limit)',
		category: 'social',
		scope: 'user',
		isToggle: false,
		unit: 'count',
	},
	cross_channel_spam_block_after: {
		key: 'cross_channel_spam_block_after',
		label: 'Cross Channel Spam Block After',
		description:
			'Distinct channels the same invite or link can be posted to within the short window before further posts are blocked (0 = off)',
		category: 'social',
		scope: 'user',
		isToggle: false,
		unit: 'count',
	},
	cross_channel_spam_freeze_after: {
		key: 'cross_channel_spam_freeze_after',
		label: 'Cross Channel Spam Freeze After',
		description:
			'Distinct communities within the short window after which a non-bot account trying the same invite or link is frozen (0 = off)',
		category: 'social',
		scope: 'user',
		isToggle: false,
		unit: 'count',
	},
	cross_channel_spam_block_after_bot: {
		key: 'cross_channel_spam_block_after_bot',
		label: 'Cross Channel Spam Block After (Bot)',
		description: 'Cross channel spam block threshold for bots; only invites are counted (0 = off)',
		category: 'social',
		scope: 'user',
		isToggle: false,
		unit: 'count',
	},
};
